import { pageWrap, tableHeaderCell, tableRow, btnPrimary, minutesFmt } from "../../utils/pageStyles";
import React, { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { AdminCreditTransactionsAPI } from "../../api/adminCreditTransactionsApi";

const TX_TYPES = [
  "reservation_debit",
  "purchase_credit",
  "manual_adjustment",
  "reservation_refund",
];

export default function AdminCreditTransactionsPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [page, setPage] = useState(0); // DataGrid page index
  const [pageSize, setPageSize] = useState(50);

  const [q, setQ] = useState("");
  const [userId, setUserId] = useState("");
  const [reservationId, setReservationId] = useState("");
  const [txType, setTxType] = useState("");

  const offset = page * pageSize;

  const columns = useMemo(
    () => [
      { field: "id", headerName: t("admin.creditTx.columns.id"), width: 90 },
      { field: "created_at", headerName: t("admin.creditTx.columns.createdAt"), width: 210 },
      { field: "type", headerName: t("admin.creditTx.columns.type"), width: 170,
        renderCell: (params) => <Chip size="small" label={params.value || "-"} />
      },
      { field: "user_id", headerName: t("admin.creditTx.columns.userId"), width: 110 },
      { field: "username", headerName: t("admin.creditTx.columns.username"), width: 160 },
      { field: "reservation_id", headerName: t("admin.creditTx.columns.reservationId"), width: 130 },
      { field: "wallet_id", headerName: t("admin.creditTx.columns.walletId"), width: 110 },
      { field: "minutes", headerName: t("admin.creditTx.columns.minutes"), width: 110 },
      { field: "change_minutes", headerName: t("admin.creditTx.columns.changeMinutes"), width: 150 },
      { field: "amount", headerName: t("admin.creditTx.columns.amount"), width: 110 },
      {
        field: "description",
        headerName: t("admin.creditTx.columns.description"),
        flex: 1,
        minWidth: 260,
        renderCell: (params) => (
          <Typography variant="body2" noWrap title={params.value || ""}>
            {params.value || "-"}
          </Typography>
        ),
      },
    ],
    [t]
  );

  const load = async () => {
    setLoading(true);
    try {
      const data = await AdminCreditTransactionsAPI.list({
        limit: pageSize,
        offset,
        q: q.trim(),
        userId: userId.trim(),
        reservationId: reservationId.trim(),
        txType,
      });
      setRows(data.items || []);
      setRowCount(data.total || 0);
    } catch (err) {
      enqueueSnackbar(t("admin.creditTx.snack.loadError"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
   
  }, [page, pageSize]);

  const onApplyFilters = () => {
    setPage(0);
    load();
  };

  const onResetFilters = () => {
    setQ("");
    setUserId("");
    setReservationId("");
    setTxType("");
    setPage(0);
    load();
  };

  return (
    <Box sx={pageWrap}>
      <Stack spacing={1}>
        <Typography variant="h5">{t("admin.creditTx.title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("admin.creditTx.subtitle")}
        </Typography>

        <Paper sx={pageWrap}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <TextField
              fullWidth
              label={t("admin.creditTx.filters.q")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("admin.creditTx.filters.qPlaceholder")}
            />
            <TextField
              label={t("admin.creditTx.filters.userId")}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              sx={{ width: { xs: "100%", md: 180 } }}
            />
            <TextField
              label={t("admin.creditTx.filters.reservationId")}
              value={reservationId}
              onChange={(e) => setReservationId(e.target.value)}
              sx={{ width: { xs: "100%", md: 180 } }}
            />
            <TextField
              select
              label={t("admin.creditTx.filters.type")}
              value={txType}
              onChange={(e) => setTxType(e.target.value)}
              sx={{ width: { xs: "100%", md: 220 } }}
            >
              <MenuItem value="">{t("common.all")}</MenuItem>
              {TX_TYPES.map((x) => (
                <MenuItem key={x} value={x}>
                  {x}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
              <Button variant="contained" onClick={onApplyFilters}>
                {t("common.apply")}
              </Button>
              <Button variant="outlined" onClick={onResetFilters}>
                {t("common.reset")}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Divider />

        <Paper sx={{ height: 680 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            slots={{
              loadingOverlay: () => (
                <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">{t("common.loading")}</Typography>
                </Box>
              ),
            }}
          />
        </Paper>
      </Stack>
    </Box>
  );
}
